import { useCallback, useMemo } from 'react';
import {
  useSorApi,
  type SorQueryPayload,
  type SorQueryResponse,
  type SorRecord,
} from '@circuitry-ai/doc-ui';
import type { WorkQueueDraft } from './work-queue-editor-utils';
import {
  buildClaimActionData,
  buildWorkQueueRecordData,
  findClaimAction,
  isClaimActionEnabled,
  mapQueueEntityRecord,
  mapWorkQueueRecord,
  type WorkQueueEntityRecord,
  type WorkQueueRecordLike,
} from './work-queues-api-mappers';
import {
  DIRECTORY_ROLE_DEFINITION,
  DIRECTORY_USER_DEFINITION,
  WORK_QUEUE_ACTION_DEFINITION,
  WORK_QUEUE_ENTITY_DEFINITION,
  WORK_QUEUE_MEMBER_DEFINITION,
  WORK_QUEUE_RECORD_DEFINITION,
  type DirectoryRoleData,
  type DirectoryUserData,
  type WorkQueue,
  type WorkQueueActionRecordData,
  type WorkQueueEntityRecordData,
  type WorkQueueEntityType,
  type WorkQueueMemberRecordData,
  type WorkQueueRecordData,
  type WorkQueueTeamRole,
  type WorkQueueTeamRoleMember,
} from './work-queues-types';

const PAGE_SIZE = 25;
// Safety stop so a bad `total` can't loop forever
const MAX_PAGES = 40;

type MemberRecord = SorRecord<WorkQueueMemberRecordData>;
type ActionRecord = SorRecord<WorkQueueActionRecordData>;

/** Pulls the `message` out of a SOR error ("422 Unprocessable Entity: {json}") for toasts */
export function getSorErrorMessage(error: unknown, fallback: string): string {
  const raw = error instanceof Error ? error.message : '';
  const jsonStart = raw.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const body = JSON.parse(raw.slice(jsonStart)) as {
        message?: string;
        detail?: unknown;
      };
      if (body.message) return body.message;
      if (typeof body.detail === 'string') return body.detail;
    } catch {
      // not JSON; fall through
    }
  }
  return raw || fallback;
}

/** "samlp|circuitry-dev|jane@company.com" -> "jane@company.com" */
const lastSegment = (value: string) => value.split('|').pop() ?? value;

const MEMBERS_BULK_URL = `/api/v1/records/bulk?definition=${encodeURIComponent(
  WORK_QUEUE_MEMBER_DEFINITION
)}`;

type BulkCreateResult = { created: number; failed: number; errors: unknown[] };

/** queueId -> number of enabled queue_member records */
function countEnabledMembersByQueue(
  members: MemberRecord[]
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const member of members) {
    const queueId = member.data?.queueId;
    if (queueId && member.data.enabled !== false) {
      counts.set(queueId, (counts.get(queueId) ?? 0) + 1);
    }
  }
  return counts;
}

/** queueId -> enabled state of its CLAIM queue_action record */
function getClaimEnabledByQueue(actions: ActionRecord[]): Map<string, boolean> {
  const byQueue = new Map<string, ActionRecord[]>();
  for (const action of actions) {
    const queueId = action.data?.queueId;
    if (queueId) {
      byQueue.set(queueId, [...(byQueue.get(queueId) ?? []), action]);
    }
  }
  const enabled = new Map<string, boolean>();
  byQueue.forEach((queueActions, queueId) => {
    const claim = findClaimAction(queueActions);
    if (claim) {
      enabled.set(queueId, claim.data.enabled !== false);
    }
  });
  return enabled;
}

type SaveWorkQueueResult = {
  queue: WorkQueue;
  /** Set when the queue saved but syncing queue_member records failed */
  memberSyncError?: string;
  /** Set when the queue saved but its self-assign (CLAIM) queue_action couldn't be saved */
  claimActionError?: string;
};

/**
 * Work Queues on the SOR records API:
 * - queues:       core.workqueue.queue records
 * - members:      core.workqueue.queue_member records, one USER record per selected team member
 * - entity types: core.workqueue.queue_entities records (uuid = data.definitionId = assignmentPolicies[].sourceDefinitionId)
 * - team roles:   core.directory.role records (data.key = roleKey)
 * - role users:   core.directory.user records whose data.roleIds contains the role record id
 */
export function useWorkQueuesApi() {
  const { sorFetch, queryRecords, connectSor } = useSorApi();

  const queryAll = useCallback(
    async <TData>(
      payload: Omit<SorQueryPayload, 'page'>,
      signal?: AbortSignal
    ) => {
      const items: SorRecord<TData>[] = [];
      for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex++) {
        const response = await queryRecords<SorQueryResponse<TData>>(
          {
            include_deleted: false,
            ...payload,
            page: { limit: PAGE_SIZE, offset: items.length },
          },
          signal
        );
        const pageItems = response?.items ?? [];
        items.push(...pageItems);
        if (!pageItems.length || items.length >= (response?.total ?? 0)) {
          break;
        }
      }
      return items;
    },
    [queryRecords]
  );

  const fetchQueues = useCallback(
    async (signal?: AbortSignal) => {
      const [records, memberCounts, claimEnabled] = await Promise.all([
        queryAll<WorkQueueRecordData>(
          {
            definition: WORK_QUEUE_RECORD_DEFINITION,
            filter: null,
            search: null,
            state: null,
          },
          signal
        ),
        // One query for all queues' members; the list still loads if it fails
        queryAll<WorkQueueMemberRecordData>(
          { definition: WORK_QUEUE_MEMBER_DEFINITION, filter: null },
          signal
        )
          .then(countEnabledMembersByQueue)
          .catch((error: unknown) => {
            if (!signal?.aborted) {
              console.error('Failed to load queue members', error);
            }
            return null;
          }),
        // Self-assign lives in queue_action records; the list still loads if they fail
        queryAll<WorkQueueActionRecordData>(
          { definition: WORK_QUEUE_ACTION_DEFINITION, filter: null },
          signal
        )
          .then(getClaimEnabledByQueue)
          .catch((error: unknown) => {
            if (!signal?.aborted) {
              console.error('Failed to load queue actions', error);
            }
            return new Map<string, boolean>();
          }),
      ]);
      return records.map((record) => ({
        ...mapWorkQueueRecord(record, {
          allowSelfAssign: claimEnabled.get(record.id) ?? false,
        }),
        member_count: memberCounts
          ? memberCounts.get(record.id) ?? 0
          : undefined,
      }));
    },
    [queryAll]
  );

  // Entity types = queue_entities records; data.definitionId is saved as assignmentPolicies[].sourceDefinitionId
  const fetchEntityTypes = useCallback(
    async (signal?: AbortSignal): Promise<WorkQueueEntityType[]> => {
      const records = (await queryAll<WorkQueueEntityRecordData>(
        {
          definition: WORK_QUEUE_ENTITY_DEFINITION,
          filter: null,
          sort: [],
          search: null,
          state: null,
          expand: ['definition'],
        },
        signal
      )) as WorkQueueEntityRecord[];
      return records
        .map(mapQueueEntityRecord)
        .filter((entityType) => entityType.uuid)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    [queryAll]
  );

  /** Switches the entity type's queue_entities record; returns the type with the new revision */
  const setEntityTypeEnabled = useCallback(
    async (
      entityType: WorkQueueEntityType,
      enabled: boolean
    ): Promise<WorkQueueEntityType> => {
      const record = await sorFetch<SorRecord<WorkQueueEntityRecordData>>(
        `/api/v1/records/${entityType.record_id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            data: { enabled },
            expected_revision: entityType.record_revision,
          }),
        }
      );
      return {
        ...entityType,
        is_enabled: record?.data?.enabled ?? enabled,
        record_revision: record?.revision ?? entityType.record_revision + 1,
      };
    },
    [sorFetch]
  );

  const fetchTeamRoles = useCallback(
    async (signal?: AbortSignal): Promise<WorkQueueTeamRole[]> => {
      const records = await queryAll<DirectoryRoleData>(
        {
          definition: DIRECTORY_ROLE_DEFINITION,
          filter: null,
          search: null,
          state: null,
        },
        signal
      );
      return records
        .filter((record) => record.data?.active !== false)
        .map((record) => {
          const key = record.data?.key ?? record.natural_key ?? '';
          return {
            id: record.id,
            name: key,
            label: record.data?.name ?? record.display ?? key,
          };
        })
        .filter((role) => role.name)
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    [queryAll]
  );

  /** Users assigned to a role; user_id is the core.directory.user record id (saved as queue_member.userId) */
  const fetchRoleMembers = useCallback(
    async (
      role: WorkQueueTeamRole,
      signal?: AbortSignal
    ): Promise<WorkQueueTeamRoleMember[]> => {
      const records = await queryAll<DirectoryUserData>(
        {
          definition: DIRECTORY_USER_DEFINITION,
          filter: { field: 'data.roleIds', op: 'jsoncontains', value: role.id },
          search: null,
          state: null,
        },
        signal
      );
      return records
        .map((record) => {
          const name = lastSegment(
            record.data?.displayName ?? record.display ?? record.id
          );
          const subject = record.data?.subjectId
            ? lastSegment(record.data.subjectId)
            : '';
          return {
            user_id: record.id,
            name,
            email: subject.includes('@') && subject !== name ? subject : '',
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    [queryAll]
  );

  const fetchQueueMembers = useCallback(
    async (queueId: string): Promise<MemberRecord[]> => {
      const members = await queryAll<WorkQueueMemberRecordData>({
        definition: WORK_QUEUE_MEMBER_DEFINITION,
        filter: { field: 'data.queueId', op: 'eq', value: queueId },
      });
      // Guard: syncQueueMembers deletes stale members, so never act on another queue's records
      return members.filter((member) => member.data?.queueId === queueId);
    },
    [queryAll]
  );

  const fetchQueueActions = useCallback(
    async (queueId: string): Promise<ActionRecord[]> => {
      const actions = await queryAll<WorkQueueActionRecordData>({
        definition: WORK_QUEUE_ACTION_DEFINITION,
        filter: { field: 'data.queueId', op: 'eq', value: queueId },
      });
      // Guard: deleteQueue deletes these, so never act on another queue's records
      return actions.filter((action) => action.data?.queueId === queueId);
    },
    [queryAll]
  );

  /** Creates the queue's CLAIM queue_action record, or switches its `enabled` to match */
  const syncClaimAction = useCallback(
    async (
      queueId: string,
      enabled: boolean,
      isNewQueue: boolean
    ): Promise<void> => {
      const claim = isNewQueue
        ? undefined
        : findClaimAction(await fetchQueueActions(queueId));
      if (!claim) {
        await sorFetch(
          `/api/v1/records?definition=${encodeURIComponent(
            WORK_QUEUE_ACTION_DEFINITION
          )}`,
          {
            method: 'POST',
            body: JSON.stringify({
              data: buildClaimActionData(queueId, enabled),
            }),
          }
        );
        return;
      }
      if ((claim.data.enabled !== false) !== enabled) {
        await sorFetch(`/api/v1/records/${claim.id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            data: { enabled },
            expected_revision: claim.revision,
          }),
        });
      }
    },
    [fetchQueueActions, sorFetch]
  );

  /** userIds of the queue's enabled USER members, to preselect them when editing */
  const fetchQueueMemberUserIds = useCallback(
    async (queueId: string): Promise<string[]> => {
      const members = await fetchQueueMembers(queueId);
      return members
        .filter(
          (member) =>
            member.data?.subjectType === 'USER' &&
            member.data.enabled !== false &&
            !!member.data.userId
        )
        .map((member) => member.data.userId as string);
    },
    [fetchQueueMembers]
  );

  /**
   * Makes the queue's USER members match the selected users: creates missing ones (bulk),
   * re-enables disabled ones and deletes unselected ones. ROLE members created by earlier versions
   * of this screen (memberKey "role:*") are removed; other ROLE members are left alone.
   */
  const syncQueueMembers = useCallback(
    async (
      queueId: string,
      userIds: string[],
      isNewQueue: boolean
    ): Promise<number> => {
      const existing = isNewQueue ? [] : await fetchQueueMembers(queueId);
      const selected = new Set(userIds);
      const userMembers = existing.filter(
        (member) => member.data?.subjectType === 'USER'
      );
      const existingUserIds = new Set(
        userMembers.map((member) => member.data.userId)
      );

      const toCreate = userIds.filter((userId) => !existingUserIds.has(userId));
      const toEnable = userMembers.filter(
        (member) =>
          selected.has(member.data.userId ?? '') &&
          member.data.enabled === false
      );
      const toDelete = existing.filter((member) =>
        member.data?.subjectType === 'USER'
          ? !selected.has(member.data.userId ?? '')
          : member.data?.memberKey?.startsWith('role:')
      );

      if (toCreate.length) {
        const records = toCreate.map((userId) => {
          const data: WorkQueueMemberRecordData = {
            queueId,
            memberKey: `user:${userId}`,
            subjectType: 'USER',
            userId,
            // The member schema only accepts USER members with roleKey and roleId both null
            roleKey: null,
            roleId: null,
            queueRole: 'AGENT',
            enabled: true,
          };
          return { data };
        });
        const result = await sorFetch<BulkCreateResult>(MEMBERS_BULK_URL, {
          method: 'POST',
          body: JSON.stringify({ atomic: true, records }),
        });
        if (result?.failed) {
          const errors = JSON.stringify(result.errors);
          throw new Error(
            `${result.failed} queue member(s) could not be created: ${errors}`
          );
        }
      }

      await Promise.all([
        ...toEnable.map((member) =>
          sorFetch(`/api/v1/records/${member.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
              data: { enabled: true },
              expected_revision: member.revision,
            }),
          })
        ),
        ...toDelete.map((member) =>
          sorFetch<void>(
            `/api/v1/records/${member.id}?expected_revision=${member.revision}`,
            { method: 'DELETE' }
          )
        ),
      ]);

      // Selected users plus enabled ROLE members this screen doesn't manage (kept above)
      const keptRoleMembers = existing.filter(
        (member) =>
          member.data?.subjectType === 'ROLE' &&
          !member.data.memberKey?.startsWith('role:') &&
          member.data.enabled !== false
      ).length;
      return userIds.length + keptRoleMembers;
    },
    [fetchQueueMembers, sorFetch]
  );

  const saveQueue = useCallback(
    async ({
      draft,
      existing,
      entityType,
      existingQueueKeys,
    }: {
      draft: WorkQueueDraft;
      existing?: WorkQueue;
      entityType?: WorkQueueEntityType;
      existingQueueKeys: string[];
    }): Promise<SaveWorkQueueResult> => {
      const data = buildWorkQueueRecordData(draft, {
        existing,
        entityType,
        existingQueueKeys,
      });
      const record = existing?.source_record
        ? await sorFetch<WorkQueueRecordLike>(
            `/api/v1/records/${existing.source_record.id}`,
            {
              method: 'PUT',
              body: JSON.stringify({
                data,
                expected_revision: existing.source_record.revision,
              }),
            }
          )
        : await sorFetch<WorkQueueRecordLike>(
            `/api/v1/records?definition=${encodeURIComponent(
              WORK_QUEUE_RECORD_DEFINITION
            )}`,
            { method: 'POST', body: JSON.stringify({ data }) }
          );
      const allowSelfAssign = isClaimActionEnabled(draft);
      const result: SaveWorkQueueResult = {
        queue: mapWorkQueueRecord(record, { allowSelfAssign }),
      };

      const [memberSync, claimSync] = await Promise.allSettled([
        syncQueueMembers(record.id, draft.member_ids, !existing),
        syncClaimAction(record.id, allowSelfAssign, !existing),
      ]);
      if (memberSync.status === 'fulfilled') {
        result.queue.member_count = memberSync.value;
      } else {
        console.error('Failed to sync work queue members', memberSync.reason);
        result.memberSyncError = getSorErrorMessage(
          memberSync.reason,
          'Failed to save queue members.'
        );
      }
      if (claimSync.status === 'rejected') {
        console.error(
          'Failed to save the queue claim action',
          claimSync.reason
        );
        result.claimActionError = getSorErrorMessage(
          claimSync.reason,
          'Failed to save the self-assign setting.'
        );
        // Reflect what's stored: the previous value when editing, off for a new queue
        result.queue.routing_options.allow_self_assign =
          existing?.routing_options.allow_self_assign ?? false;
      }
      return result;
    },
    [sorFetch, syncQueueMembers, syncClaimAction]
  );

  /**
   * Deletes the queue's queue_member and queue_action records first (SOR refuses to delete a queue
   * they still reference), then soft-deletes the queue record (DELETE ?hard=false, as the SOR app does).
   * Any failure is thrown, and the queue is never deleted while members remain.
   */
  const deleteQueue = useCallback(
    async (queue: WorkQueue): Promise<void> => {
      const [members, actions] = await Promise.all([
        fetchQueueMembers(queue.queue_id),
        fetchQueueActions(queue.queue_id),
      ]);
      await Promise.all(
        [...members, ...actions].map((record) =>
          sorFetch<void>(`/api/v1/records/${record.id}`, { method: 'DELETE' })
        )
      );

      const params = new URLSearchParams({ hard: 'false' });
      if (queue.source_record) {
        params.set('expected_revision', String(queue.source_record.revision));
      }
      await sorFetch<void>(`/api/v1/records/${queue.queue_id}?${params}`, {
        method: 'DELETE',
      });
    },
    [fetchQueueMembers, fetchQueueActions, sorFetch]
  );

  return useMemo(
    () => ({
      fetchQueues,
      fetchEntityTypes,
      fetchTeamRoles,
      fetchRoleMembers,
      fetchQueueMemberUserIds,
      saveQueue,
      setEntityTypeEnabled,
      deleteQueue,
      connectSor,
    }),
    [
      fetchQueues,
      fetchEntityTypes,
      fetchTeamRoles,
      fetchRoleMembers,
      fetchQueueMemberUserIds,
      saveQueue,
      setEntityTypeEnabled,
      deleteQueue,
      connectSor,
    ]
  );
}
